/**
 * YACHT® Cart Drawer & AJAX Cart Engine
 */

class YachtCartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.itemsContainer = document.querySelector('[data-cart-drawer-items]');
    this.subtotalElement = document.querySelector('[data-cart-subtotal]');
    this.countBadges = document.querySelectorAll('[data-cart-count-badge]');
    this.shippingBar = document.querySelector('[data-free-shipping-progress]');
    this.shippingText = document.querySelector('[data-free-shipping-text]');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.refreshCartState();
  }

  bindEvents() {
    // Open cart drawer triggers
    document.querySelectorAll('[data-open-cart-drawer]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close cart drawer triggers
    document.querySelectorAll('[data-close-cart-drawer]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Handle AJAX Add to Cart from PDP or quick buys
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.matches('[data-type="add-to-cart-form"]') || form.action.includes('/cart/add')) {
        e.preventDefault();
        this.addToCart(form);
      }
    });

    // Handle item removal & quantity changes within drawer
    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          const lineKey = removeBtn.getAttribute('data-line-key');
          this.updateQuantity(lineKey, 0);
        }
      });

      this.itemsContainer.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-item-qty]');
        if (input) {
          const lineKey = input.getAttribute('data-line-key');
          const qty = parseInt(input.value, 10);
          this.updateQuantity(lineKey, qty);
        }
      });
    }
  }

  open() {
    if (this.drawer) {
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  async addToCart(form) {
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (response.ok) {
        await this.refreshCartState();
        this.open();
      } else {
        const err = await response.json();
        alert(err.description || 'Could not add item to cart.');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
      }
    }
  }

  async updateQuantity(lineKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });

      if (response.ok) {
        await this.refreshCartState();
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  }

  async refreshCartState() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  renderCart(cart) {
    // Update count badges
    this.countBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    // Update Subtotal
    if (this.subtotalElement) {
      const formattedPrice = this.formatMoney(cart.total_price);
      this.subtotalElement.textContent = formattedPrice;
    }

    // Update Free Shipping Progress
    if (this.shippingBar && window.YACHT_CONFIG) {
      const thresholdCents = (window.YACHT_CONFIG.freeShippingThreshold || 499) * 100;
      const progressPct = Math.min(100, Math.round((cart.total_price / thresholdCents) * 100));
      this.shippingBar.style.width = `${progressPct}%`;

      if (this.shippingText) {
        if (cart.total_price >= thresholdCents) {
          this.shippingText.textContent = window.YACHT_CONFIG.freeShippingSuccessMsg || 'Complimentary shipping unlocked.';
        } else {
          const remainingCents = thresholdCents - cart.total_price;
          const remainingFormatted = this.formatMoney(remainingCents);
          this.shippingText.textContent = `Add ${remainingFormatted} more for complimentary shipping.`;
        }
      }
    }

    // Render items HTML
    if (this.itemsContainer) {
      if (cart.item_count === 0) {
        this.itemsContainer.innerHTML = `
          <div style="text-align: center; padding: 3rem 0; color: var(--color-slate);">
            <p style="margin-bottom: 1.5rem;">Your cart is currently empty.</p>
            <a href="/collections/all" class="btn btn--gold">Explore the Collection</a>
          </div>
        `;
      } else {
        let itemsHtml = '';
        cart.items.forEach(item => {
          itemsHtml += `
            <div class="cart-item">
              <div class="cart-item__img-wrap">
                <img src="${item.featured_image ? item.featured_image.url : ''}" alt="${item.title}" class="cart-item__img" />
              </div>
              <div class="cart-item__details">
                <div class="cart-item__title">${item.product_title}</div>
                ${item.variant_title ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
                <div style="font-weight: 600; margin-bottom: 0.5rem; color: var(--color-navy);">${this.formatMoney(item.final_line_price)}</div>
                <div class="cart-item__actions">
                  <div class="quantity-stepper" style="height: 32px; width: 90px;" data-quantity-stepper>
                    <button type="button" class="quantity-stepper__btn" data-quantity-minus>-</button>
                    <input type="number" class="quantity-stepper__input" data-cart-item-qty data-line-key="${item.key}" value="${item.quantity}" min="1" />
                    <button type="button" class="quantity-stepper__btn" data-quantity-plus>+</button>
                  </div>
                  <button type="button" class="cart-item__remove-btn" data-cart-remove-item data-line-key="${item.key}">Remove</button>
                </div>
              </div>
            </div>
          `;
        });
        this.itemsContainer.innerHTML = itemsHtml;
        initQuantitySteppers();
      }
    }
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Standard fallback formatter: ₹(cents / 100)
    return `₹${(cents / 100).toLocaleString('en-IN')}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.yachtCart = new YachtCartDrawer();
});
